import { Input } from "antd";
import restClient from "api/restClient";
import { Button } from "components/Button/Button";
import { IconCollection } from "components/Icon/IconCollection";
import { Markdown } from "components/Form/components/Markdown/Markdown";
import NotificationService from "components/Notification/Notification.service";
import RestErrorNotification from "components/Notification/RestErrorNotification";
import { showConfirmModal } from "components/Modal/showConfirmModal";
import { BlockingNavigationModal } from "components/Prompt/BlockingNavigationModal";
import { NAVIGATION_PRIORITIES } from "components/Prompt/NavigationPromptContext";
import { SpinnerAnimation } from "components/SpinnerAnimation/SpinnerAnimation";
import { TimeAgoWithDateTimeFormatter } from "components/TimeAgo/TimeAgoWithDateTimeFormatter";
import { DocsInfoTooltip } from "components/Tooltip/DocsInfoTooltip";
import { FeConfigType } from "config/config";
import { getUserData } from "containers/Auth/Auth.selectors";
import { actions as resourcesActions } from "containers/Resources/Resources.reducer";
import { useMigrateOnSaveParams } from "containers/Resources/screens/ResourcesSingle/useCanCommitOnSave";
import { Location } from "history";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router";
import { actions as dataStoreAction } from "utils/DataStore/DataStore.reducer";
import strings from "utils/strings";
import { getIri, getSearchObj } from "utils/utils";
import { computeCommentScrollDelta, getScrollParent } from "./commentScroll";
import { buildDiscardCommentConfirm, shouldConfirmDiscardDraft } from "./commentCancel";
import {
    commentDraftKey,
    pruneCommentDrafts,
    readCommentDraft,
    writeCommentDraft,
} from "./commentDraftStore";
import styles from "./CommentsPanel.module.scss";

/**
 * One row returned by the generic `record_comment_plain_list` GraphQL query.
 *
 * Typed locally rather than importing from `graphql/generated` because the
 * generated types are (re)produced by the build-time codegen against the
 * deployed backend schema; this keeps local typecheck green while the panel
 * is authored ahead of the first regeneration.
 */
interface RecordCommentRow {
    id: string;
    body: string;
    author_identity_id: string | null;
    author_identity_id_identity: { name: string } | null;
    assignee_identity_id: string | null;
    parent_comment_id: string | null;
    resolved_at: string | null;
    resolved_by: string | null;
    created_at: string;
}

interface CommentsPanelProps {
    id: string;
    isDisabled: boolean;
    setHasUnsavedChanges: (hasUnsavedChanges: boolean) => void;
    resourceName?: FeConfigType;
    /**
     * Bumped by the parent record screen's manual Refresh. Added to the comment
     * fetch effect deps so a record refresh also re-fetches the comment list,
     * keeping it in sync with the comments-tab badge count (which rides the
     * record body re-fetch). See operator-task-fe-record-refresh-stale-comments.
     */
    refreshNonce?: number;
    /**
     * Same-record / in-page navigation escape hatch, forwarded to the pending-
     * comment navigation guard. Lets same-record URL canonicalizations (the
     * `history.replace` that strips a sub-page flag) through without prompting,
     * so the guard fires only on genuine navigation away — reusing the exact
     * predicate the parent record form uses (see isSameRecordViewNavigation in
     * ResourcesSingle) rather than a parallel check.
     */
    allowNavigation?: (nextLocation: Location) => boolean;
}

/**
 * Long-comment-list readability (operator-task-fe-record-comments-display-long-list-readability).
 * Comments are kept in their chronological (oldest→newest) reading order — the
 * app-wide convention shared with the wake-snapshot comment tail, the
 * `get_record` `comments` key and the docs — while the two long-thread pains are
 * addressed at the view layer (Stefan's chosen option A, 2026-08-26):
 *  - INITIAL_VISIBLE_COMMENTS bounds the initial DOM render regardless of thread
 *    length; older comments are revealed on demand via a "Show older comments"
 *    affordance (OLDER_PAGE_SIZE at a time).
 *  - the newest comment (bottom of the chronological list, next to the composer)
 *    is scrolled into view on load and after posting, so the most-important
 *    comment is visible without a long manual scroll.
 * This is deliberately a pure view-layer bound rather than server-side offset
 * paging: it preserves the exact full-list fetch the comments-tab badge count
 * rides on (no count regression) and the stated pain is render height / scroll
 * position, not fetch size. Server-side paging can be layered on later if
 * threads ever grow to thousands.
 */
const INITIAL_VISIBLE_COMMENTS = 10;
const OLDER_PAGE_SIZE = 20;
// After an armed scroll-to-newest, keep re-anchoring the newest comment as the
// list's height settles (async markdown / syntax highlighting / images grow
// comments AFTER the first frame). Bounded so we only correct during a brief
// settle window and never fight a user who scrolls away afterwards.
const SCROLL_SETTLE_WINDOW_MS = 1500;

/**
 * Reply-threading (operator-task-record-comments-ui-enhancements). Replies are
 * grouped under their parent via `record_comment.parent_comment_id` and rendered
 * indented. The DATA nesting is unbounded (a reply can target any comment), but
 * the VISUAL indent is capped at this depth so a long reply chain cannot walk
 * the thread off the right edge — replies deeper than the cap keep rendering at
 * the cap's indent level.
 */
const MAX_THREAD_DEPTH = 4;

const authorLabel = (comment: RecordCommentRow, currentIdentityId?: string): string => {
    const authorIdentityId = comment.author_identity_id;
    if (!authorIdentityId) {
        return "System";
    }
    if (currentIdentityId && authorIdentityId === currentIdentityId) {
        return "You";
    }
    // Prefer the resolved identity name; fall back to a short id when the
    // relationship is unreadable (RBAC) or absent.
    const resolvedName = comment.author_identity_id_identity?.name;
    if (resolvedName) {
        return resolvedName;
    }
    return `Identity ${authorIdentityId.slice(0, 8)}`;
};

export const CommentsPanel = ({
    id,
    isDisabled,
    setHasUnsavedChanges,
    resourceName,
    refreshNonce,
    allowNavigation,
}: CommentsPanelProps) => {
    const dispatch = useDispatch();
    const history = useHistory();

    const key = `recordCommentList-${id}`;

    const [isLoading, setLoading] = useState(false);
    // Dedicated in-flight flag for the *post comment* request, kept separate
    // from the panel-wide isLoading (list fetch / delete) so the submit button
    // reflects submission state precisely. Drives both the button's pending
    // affordance (spinner) and its disabled state, and guards postComment
    // against re-entry (double-click / future Ctrl+Enter) posting a duplicate.
    const [isSubmitting, setSubmitting] = useState(false);
    // Lock the whole composer (textarea, Cancel, reply-Cancel), not only the
    // submit button, while a post is in flight: the request already captured
    // the draft, so an edit made mid-flight would be silently dropped.
    const composerLocked = isDisabled || isSubmitting;
    const [draft, setDraft] = useState("");
    // The composer draft is also kept in localStorage (commentDraftStore.ts) so
    // an involuntary reload cannot lose it. draftStorageKey is the key the
    // current `draft` state was loaded for; saving is skipped until it matches
    // the current record's key, so one record's draft is never written under
    // another record's key while the record changes.
    const [draftStorageKey, setDraftStorageKey] = useState<string | null>(null);
    // True while the composer shows a draft restored from storage.
    const [draftRestored, setDraftRestored] = useState(false);
    // Reply target of a restored draft, applied once the comment list is loaded.
    const pendingReplyIdRef = useRef<string | null>(null);
    // When set, the composer posts a REPLY to this comment (its id becomes the
    // new comment's parent_comment_id); null means a new top-level comment.
    const [replyTo, setReplyTo] = useState<RecordCommentRow | null>(null);
    // The composer wrapper — scrolled into view + focused when a reply is armed.
    const composerRef = useRef<HTMLDivElement | null>(null);

    // A non-empty (non-whitespace) draft is a pending comment that would be lost
    // on navigate-away. Drives both the in-page tab-switch guard (via
    // setHasUnsavedChanges) and the route-change / beforeunload guard below.
    const hasPendingComment = draft.trim().length > 0;

    const userData = useSelector(getUserData);
    const currentIdentityId: string | undefined = userData?.identity_id;

    // How many of the most-recent comments to render. Bounds the initial DOM on
    // long threads; "Show older comments" reveals OLDER_PAGE_SIZE more each click.
    const [visibleCount, setVisibleCount] = useState(INITIAL_VISIBLE_COMMENTS);
    // The newest (bottom-most) rendered comment — scrolled into view on load and
    // after posting so the most-important comment is reachable without scrolling.
    const newestCommentRef = useRef<HTMLDivElement | null>(null);
    // The scrolling comment-list container. Observed for late height changes so
    // the newest-comment anchor can be re-applied as markdown/highlight settle.
    const commentListRef = useRef<HTMLDivElement | null>(null);
    // Armed on initial record load and after the user posts, so those (and only
    // those) events anchor the view to the newest comment; a background refetch
    // (refreshNonce) or a "show older" expansion must NOT yank the scroll.
    const shouldScrollToNewest = useRef(true);

    const getRecordCommentListSelector = (state) => state?.dataStore?.[key];
    const commentList: RecordCommentRow[] = useSelector(getRecordCommentListSelector)
        ?.record_comment_plain_list;

    // A non-empty draft counts as unsaved changes so tab-switching is guarded.
    useEffect(() => {
        setHasUnsavedChanges(hasPendingComment);
    }, [hasPendingComment, setHasUnsavedChanges]);

    const getData = useCallback(() => {
        setLoading(true);
        const filterStr = JSON.stringify(getSearchObj({ field: "record_id", value: id, op: "eq" }));
        new Promise((resolve, reject) => {
            dispatch({
                promise: { resolve, reject },
                type: dataStoreAction.START_FETCH_DATA,
                key,
                params: {
                    query: `query($filter: String, $order: String, $limit: Int, $offset: Int) {
                        record_comment_plain_list(filter:$filter,order:$order,limit:$limit,offset:$offset){
                            id
                            body
                            author_identity_id
                            author_identity_id_identity { name }
                            assignee_identity_id
                            parent_comment_id
                            resolved_at
                            resolved_by
                            created_at
                        },
                    }`,
                    variables: { filter: filterStr, order: "+created_at", offset: 0 },
                },
            });
        })
            .then((data: any) => {
                const count = data?.record_comment_plain_list?.length;
                dispatch({
                    type: resourcesActions.STORE_RESOURCE_DATA_WITH_PARTIAL_UPDATE,
                    iri: getIri(resourceName, id),
                    response: {
                        record_comment_list_record_id: {
                            record_comment_count: count,
                        },
                    },
                });
            })
            .catch((error) => error)
            .finally(() => setLoading(false));
    }, [dispatch, id, key, resourceName]);

    // Re-fetch on mount / id change AND whenever the parent bumps refreshNonce
    // (manual record Refresh), so the list stays in sync with the badge count.
    useEffect(() => {
        if (id) {
            getData();
        }
    }, [getData, id, refreshNonce]);

    const migrateOnSaveParams = useMigrateOnSaveParams();
    const params = useMemo(
        () => ({ commit: false, ...migrateOnSaveParams }),
        [migrateOnSaveParams]
    );

    const postComment = useCallback(() => {
        const body = draft.trim();
        // Re-entry guard: never fire a second create while one is already in
        // flight (a rapid double-click, or a future Ctrl+Enter path) — this is
        // what previously posted the same comment multiple times.
        if (!body || isSubmitting) {
            return;
        }
        // A reply carries its target's id as parent_comment_id; a top-level
        // comment sends null. The backend `New_Record_Comment` input accepts
        // parent_comment_id (nullable) on the same create mutation.
        const parentCommentId = replyTo?.id ?? null;
        setSubmitting(true);
        restClient
            .post(
                "/graphql",
                {
                    query: `mutation create_record_comment($record_id: String!, $body: String!, $parent_comment_id: String) {
                        create_record_comment(body: {record_id: $record_id, body: $body, parent_comment_id: $parent_comment_id}) {
                            id
                        }
                    }`,
                    variables: { record_id: id, body, parent_comment_id: parentCommentId },
                },
                { params }
            )
            .then(() => {
                setDraft("");
                setDraftRestored(false);
                setReplyTo(null);
                // The freshly-posted comment is the newest — anchor to it once
                // the refetched list renders.
                shouldScrollToNewest.current = true;
                getData();
            })
            .catch((error) =>
                NotificationService.error(
                    <RestErrorNotification
                        titleOverride="Sorry, couldn't post comment"
                        error={error}
                    />
                )
            )
            .finally(() => setSubmitting(false));
    }, [draft, id, params, getData, isSubmitting, replyTo]);

    const handleDraftKeyDown = useCallback(
        (event: React.KeyboardEvent<HTMLTextAreaElement>) => {
            // Ctrl+Enter / Cmd+Enter posts the comment — the conventional
            // submit-multiline-text shortcut. Plain Enter keeps its default
            // behaviour (newline in the multi-line composer). postComment
            // itself no-ops on an empty draft or an in-flight submit, so no
            // extra guarding is needed here; and a disabled composer never
            // receives keydown events.
            if (event.key === "Enter" && (event.ctrlKey || event.metaKey)) {
                event.preventDefault();
                postComment();
            }
        },
        [postComment]
    );

    const deleteComment = useCallback(
        (commentId: string) => {
            setLoading(true);
            restClient
                .post(
                    "/graphql",
                    {
                        query: `mutation delete_record_comment($id: String!){
                            delete_record_comment(id: $id)
                        }`,
                        variables: { id: commentId },
                    },
                    { params }
                )
                .then(() => getData())
                .catch((error) =>
                    NotificationService.error(
                        <RestErrorNotification
                            titleOverride="Sorry, couldn't delete comment"
                            error={error}
                        />
                    )
                )
                .finally(() => setLoading(false));
        },
        [params, getData]
    );

    // Cancel the new-comment composer: discard the in-progress draft and leave
    // any reply mode. Because discarding loses typed text it is guarded by the
    // shared confirmation modal when the draft holds real text (the same
    // data-safety guard pattern used elsewhere in the app); an empty draft
    // clears with no prompt.
    const cancelComposer = useCallback(() => {
        const discard = () => {
            setDraft("");
            setDraftRestored(false);
            setReplyTo(null);
        };
        if (shouldConfirmDiscardDraft(draft)) {
            showConfirmModal(buildDiscardCommentConfirm(discard));
        } else {
            discard();
        }
    }, [draft]);

    const comments: RecordCommentRow[] = useMemo(() => commentList ?? [], [commentList]);

    // Build the reply tree from the flat, chronologically-ordered list.
    // `parent_comment_id` groups replies under their parent; a comment whose
    // parent is null — or absent from the fetched set — is a top-level thread
    // (defensive: an orphan reply is surfaced rather than hidden). Insertion
    // order is preserved, so top-level threads and each reply group keep their
    // oldest→newest order (the app-wide comment convention).
    const { topLevel, childrenByParent } = useMemo(() => {
        const presentIds = new Set(comments.map((c) => c.id));
        const children = new Map<string, RecordCommentRow[]>();
        const roots: RecordCommentRow[] = [];
        for (const comment of comments) {
            const parentId = comment.parent_comment_id;
            if (parentId && presentIds.has(parentId)) {
                const siblings = children.get(parentId) ?? [];
                siblings.push(comment);
                children.set(parentId, siblings);
            } else {
                roots.push(comment);
            }
        }
        return { topLevel: roots, childrenByParent: children };
    }, [comments]);

    // View-layer paging bounds the number of top-level THREADS rendered (a
    // thread's replies always render with it). Chronological order preserved
    // (oldest→newest, newest thread at the bottom by the composer); older
    // threads stay one "Show older comments" click away. Preserves the exact
    // full-list fetch the comments-tab badge count rides on.
    const hiddenOlderCount = Math.max(0, topLevel.length - visibleCount);
    const visibleTopLevel = hiddenOlderCount > 0 ? topLevel.slice(hiddenOlderCount) : topLevel;

    // Switching records resets paging, clears any armed reply, and re-arms the
    // scroll-to-newest.
    useEffect(() => {
        setVisibleCount(INITIAL_VISIBLE_COMMENTS);
        setReplyTo(null);
        shouldScrollToNewest.current = true;
    }, [id]);

    // ── Durable composer draft ──
    // Load the stored draft when the record (or the viewing identity) changes.
    // Declared after the record-change reset above so it runs after it.
    const storageKey = currentIdentityId ? commentDraftKey(currentIdentityId, id) : null;
    useEffect(() => {
        if (!storageKey) return;
        pruneCommentDrafts();
        const stored = readCommentDraft(storageKey);
        setDraft(stored?.body ?? "");
        setDraftRestored(!!stored);
        pendingReplyIdRef.current = stored?.replyToId ?? null;
        setDraftStorageKey(storageKey);
    }, [storageKey]);

    // Save the draft on every change (an empty draft removes the entry).
    useEffect(() => {
        if (!storageKey || draftStorageKey !== storageKey) return;
        writeCommentDraft(storageKey, {
            body: draft,
            assigneeIdentityId: null,
            replyToId: replyTo?.id ?? pendingReplyIdRef.current,
        });
    }, [storageKey, draftStorageKey, draft, replyTo]);

    // Re-arm the reply target of a restored draft once the comments are loaded.
    // A target that no longer exists is dropped (the draft stays, top-level).
    const restoringReplyRef = useRef(false);
    useEffect(() => {
        const pendingId = pendingReplyIdRef.current;
        if (!pendingId || commentList === undefined || commentList === null) return;
        pendingReplyIdRef.current = null;
        const target = comments.find((comment) => comment.id === pendingId);
        if (target) {
            restoringReplyRef.current = true;
            setReplyTo(target);
        }
    }, [commentList, comments]);

    // When a reply is armed, bring the composer into view and focus it so the
    // user can type immediately after clicking "Reply" on a comment above.
    // A reply restored with a stored draft does not move the view.
    useEffect(() => {
        if (restoringReplyRef.current) {
            restoringReplyRef.current = false;
            return;
        }
        if (replyTo) {
            composerRef.current?.scrollIntoView({ block: "nearest" });
            composerRef.current?.querySelector("textarea")?.focus();
        }
    }, [replyTo]);

    // Single-shot: measure the newest comment against the live sticky occluders
    // and the composer, and scroll it fully into view within the record scroll
    // container. Seats the comment's bottom just above the composer, or its top
    // just below the header when the comment is taller than the visible band —
    // so the first line is never clipped (no overshoot). Pure apply (no defer);
    // the arming effect below controls WHEN this runs. See ./commentScroll.
    const applyNewestScroll = useCallback(() => {
        const el = newestCommentRef.current;
        if (!el) {
            return;
        }
        const scroller = getScrollParent(el);
        if (!scroller) {
            // No scrollable ancestor found — best-effort native fallback.
            el.scrollIntoView({ block: "nearest" });
            return;
        }
        const elRect = el.getBoundingClientRect();
        const scRect = scroller.getBoundingClientRect();
        // The sticky occluders pinned at the top of the record scroll view.
        // Two can be present: the record `.state-tabs` bar (ALWAYS sticky —
        // this is the tab row that carries the Comments tab), and the
        // `.page-header-sticky` page-header (breadcrumb + title + actions),
        // which the header only adds when it is BOTH sticky and actually
        // scroll-pinned (`isSticky && isScrollNeeded` in
        // ResourcesSinglePageHeader) — so it is frequently absent. We take
        // the LOWEST edge of whichever occluders are actually present so the
        // comment's first line is never clipped behind them, measured live so
        // pinned offsets / a taller header are accounted for without a magic
        // constant; falls back to the container top when none are present.
        const headerEls = [".state-tabs", ".page-header-sticky"]
            .map((sel) => document.querySelector<HTMLElement>(sel))
            .filter((el): el is HTMLElement => el !== null);
        const headerBottom = headerEls.length
            ? Math.max(...headerEls.map((el) => el.getBoundingClientRect().bottom))
            : scRect.top;
        const delta = computeCommentScrollDelta({
            elTop: elRect.top,
            elBottom: elRect.bottom,
            scTop: scRect.top,
            scBottom: scRect.bottom,
            composerHeight: composerRef.current?.offsetHeight ?? 0,
            headerBottom,
            gap: 8,
        });
        if (Math.abs(delta) > 1) {
            scroller.scrollBy({ top: delta });
        }
    }, []);

    // When the (armed) newest comment is present, bring it into view. Keyed on
    // the comment count and the newest id so it fires on initial load and after
    // a post/delete changes the tail — but the ref guard means only an explicitly
    // armed event (load / own post) actually scrolls.
    //
    // A SINGLE frame's measure races long threads: async markdown / syntax
    // highlighting / images keep GROWING the list AFTER the first frame, so a
    // one-shot scroll lands against a stale (shorter) height and undershoots
    // (measured live: the newest comment left 4200px below the fold on a heavy
    // thread). So we apply once next frame and then RE-APPLY on every list
    // resize as the layout settles, bounded by SCROLL_SETTLE_WINDOW_MS so we
    // only correct during the brief settle window and never fight a user who
    // scrolls away afterwards.
    const newestCommentId = comments.length > 0 ? comments[comments.length - 1].id : undefined;
    useEffect(() => {
        if (!shouldScrollToNewest.current || !newestCommentRef.current) {
            return;
        }
        shouldScrollToNewest.current = false;
        let raf = requestAnimationFrame(applyNewestScroll);
        const scheduleApply = () => {
            cancelAnimationFrame(raf);
            raf = requestAnimationFrame(applyNewestScroll);
        };
        // Observe the comment LIST (not just the newest comment): comments above
        // the newest also grow as they settle, which pushes the newest down.
        const observed = commentListRef.current;
        let observer: ResizeObserver | null = null;
        if (observed && typeof ResizeObserver !== "undefined") {
            observer = new ResizeObserver(scheduleApply);
            observer.observe(observed);
        }
        const stopAt = window.setTimeout(() => {
            observer?.disconnect();
            observer = null;
        }, SCROLL_SETTLE_WINDOW_MS);
        return () => {
            cancelAnimationFrame(raf);
            observer?.disconnect();
            window.clearTimeout(stopAt);
        };
    }, [comments.length, newestCommentId, applyNewestScroll]);

    // Recursively render a comment and its reply subtree. `depth` drives the
    // visual indent, capped at MAX_THREAD_DEPTH so a long chain cannot run off
    // the right edge.
    const renderComment = (comment: RecordCommentRow, depth: number) => {
        const isOwn =
            !!currentIdentityId && comment.author_identity_id === currentIdentityId;
        const isReplyTarget = replyTo?.id === comment.id;
        const replies = childrenByParent.get(comment.id) ?? [];
        return (
            <div className={styles.commentThread} key={comment.id}>
                <div
                    className={`${styles.comment}${isOwn ? ` ${styles.commentOwn}` : ""}${
                        isReplyTarget ? ` ${styles.commentReplyTarget}` : ""
                    }`}
                    ref={comment.id === newestCommentId ? newestCommentRef : undefined}
                >
                    <div className={styles.commentHeader}>
                        <span className={styles.author}>
                            {authorLabel(comment, currentIdentityId)}
                        </span>
                        <span className={styles.timestamp}>
                            <TimeAgoWithDateTimeFormatter datetime={comment.created_at} />
                        </span>
                    </div>
                    <Markdown
                        markdownString={comment.body ?? ""}
                        canCopy={false}
                        className={styles.commentBody}
                    />
                    {/*
                     * Per-comment action row, at the comment's BOTTOM-LEFT below
                     * the body so "reply" sits where the eye lands when reading
                     * finishes, instead of at the top
                     * (operator-task-fe-comment-actions-position-bottom-left,
                     * Margot). Reply + Delete render as icon-only buttons matching
                     * each other
                     * (operator-task-fe-comment-actions-reply-markunread-as-icons,
                     * Stefan: "they should be icon buttons"), each carrying a
                     * tooltip + aria-label so the icon stays unambiguous
                     * (ui-design-principle-buttons: the canonical `icon` variant;
                     * -affordance-interactivity: an icon-only control still reads
                     * as a button and names its action; -inline-hover-actions:
                     * quiet icon-grey).
                     */}
                    <div className={styles.commentActions}>
                        <Button
                            icon={IconCollection.arrowUpLeft}
                            variation="icon"
                            title="Reply"
                            ariaLabel="Reply"
                            onClick={() => setReplyTo(comment)}
                            disabled={isDisabled}
                            labelOnlyAria
                        />
                        <Button
                            icon={IconCollection.deleteOutline}
                            variation="icon"
                            title="Delete comment"
                            ariaLabel="Delete comment"
                            onClick={() => deleteComment(comment.id)}
                            disabled={isDisabled}
                            labelOnlyAria
                        />
                    </div>
                </div>
                {replies.length > 0 && (
                    <div
                        className={depth < MAX_THREAD_DEPTH ? styles.commentChildren : undefined}
                    >
                        {replies.map((reply) =>
                            renderComment(reply, Math.min(depth + 1, MAX_THREAD_DEPTH))
                        )}
                    </div>
                )}
            </div>
        );
    };

    return (
        <div className={styles.commentsPanel}>
            {/*
             * Guard the pending comment against navigate-away (in-app route
             * change + browser tab close/refresh via beforeunload). Reuses the
             * shared unsaved-changes machinery — the same BlockingNavigationModal
             * the parent record form and the metadata tab use — rather than a
             * parallel mechanism. An empty/whitespace-only composer does not
             * block (hasPendingComment is false).
             */}
            <BlockingNavigationModal
                navigate={history.push}
                shouldBlockNavigation={hasPendingComment}
                titleText={strings.navigateAwayUnsavedCommentPrompt}
                priority={NAVIGATION_PRIORITIES.COMMENT_FORM}
                allowNavigation={allowNavigation}
            />
            <SpinnerAnimation isLoading={isLoading} className={styles.spinnerAnimation} />
            {comments.length === 0 && !isLoading ? (
                <div className={styles.emptyState}>No comments yet.</div>
            ) : (
                <div className={styles.commentList} ref={commentListRef}>
                    {hiddenOlderCount > 0 && (
                        <Button
                            className={styles.showOlder}
                            variation="white"
                            size="small"
                            label={`Show older comments (${hiddenOlderCount} more)`}
                            onClick={() =>
                                setVisibleCount((count) => count + OLDER_PAGE_SIZE)
                            }
                        />
                    )}
                    {visibleTopLevel.map((comment) => renderComment(comment, 0))}
                </div>
            )}
            <div className={styles.newComment} ref={composerRef}>
                {draftRestored && draft.trim().length > 0 && (
                    <div className={styles.restoredDraftNote}>
                        Your unsaved comment was restored. Post it, or use Cancel to discard it.
                    </div>
                )}
                {replyTo && (
                    <div className={styles.replyingTo}>
                        <span className={styles.replyingToLabel}>
                            Replying to {authorLabel(replyTo, currentIdentityId)}
                        </span>
                        <Button
                            variation="white"
                            size="small"
                            label="Cancel"
                            onClick={() => setReplyTo(null)}
                            disabled={isSubmitting}
                        />
                    </div>
                )}
                <Input.TextArea
                    value={draft}
                    onChange={(event) => setDraft(event.target.value)}
                    onKeyDown={handleDraftKeyDown}
                    placeholder={
                        replyTo
                            ? "Write a reply (Markdown supported)…"
                            : "Write a comment (Markdown supported)…"
                    }
                    autoSize={{ minRows: 3, maxRows: 12 }}
                    disabled={composerLocked}
                />
                <div className={styles.newCommentActions}>
                    <Button
                        label={replyTo ? "Reply" : "Comment"}
                        title={
                            replyTo
                                ? "Post reply (Ctrl+Enter / ⌘+Enter)"
                                : "Post comment (Ctrl+Enter / ⌘+Enter)"
                        }
                        onClick={postComment}
                        variation="primary"
                        size="small"
                        loading={isSubmitting}
                        disabled={draft.trim().length === 0 || composerLocked}
                    />
                    {/*
                     * Cancel the in-progress comment: the secondary peer of the
                     * blue `primary` Comment (ui-design-principle-buttons).
                     * Discards the draft (guarded by a confirm when it holds
                     * text). Enabled whenever there is something to cancel — a
                     * typed draft or an armed reply.
                     */}
                    <Button
                        label="Cancel"
                        title="Discard this comment"
                        onClick={cancelComposer}
                        variation="white"
                        size="small"
                        disabled={composerLocked || (draft.trim().length === 0 && !replyTo)}
                    />
                    <DocsInfoTooltip
                        title="Record comments"
                        urlPath="/docs/accessing-and-manipulating-records#record-comments"
                    />
                </div>
            </div>
        </div>
    );
};
